import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import * as PersonasService from '../services/PersonasService';
import {PersonaFormValues} from '../types';
import {
    notifyError,
    notifySuccess,
} from '../../../components/PlatformNotification';
import {testingApiAvailable} from '../../testing-api-integration/config';

export const personasKeys = {
    all: ['testing-personas'] as const,
    lists: () => [...personasKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...personasKeys.lists(), {filters}] as const,
    details: () => [...personasKeys.all, 'detail'] as const,
    detail: (id: string) => [...personasKeys.details(), id] as const,
};

export const usePersonasQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
                        queryKey: personasKeys.list(filters),
                        queryFn: () => PersonasService.fetchRecords(filters),
                        enabled: testingApiAvailable,
                    });
};

export const usePersonaQuery = (id: string) => {
    return useQuery({
                        queryKey: personasKeys.detail(id),
                        queryFn: () => PersonasService.fetch(id),
                        enabled: testingApiAvailable && !!id,
                    });
};

export const useCreatePersonaMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: PersonasService.add,
                           onSuccess: () => {
                               notifySuccess('Persona created');
                               queryClient.invalidateQueries({queryKey: personasKeys.lists()});
                           },
                           onError: notifyError,
                       });
};

export const useUpdatePersonaMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: ({
                                            id,
                                            record
                                        }: { id: string; record: PersonaFormValues }) =>
                               PersonasService.update(id, record),
                           onSuccess: (_, {id}) => {
                               notifySuccess('Persona updated');
                               queryClient.invalidateQueries({queryKey: personasKeys.lists()});
                               queryClient.invalidateQueries({queryKey: personasKeys.detail(id)});
                           },
                           onError: notifyError,
                       });
};

export const useDeletePersonaMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: PersonasService.deleteRecord,
                           onSuccess: () => {
                               notifySuccess('Persona deleted');
                               queryClient.invalidateQueries({queryKey: personasKeys.all});
                           },
                           onError: notifyError,
                       });
};
